import QRCode from 'qrcode';
import type { SifaTeilnahme } from './types';
import { fetchTeilnahmeDetail } from './sifaService';
import { erzeugeZertifikatPdf } from './zertifikatPdf';
import { ensureEmbeddedFont } from '../pdf/embeddedFont';
import { logEmailAttempt, sendReportEmail } from '../cloudService';
import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';

/**
 * Zertifikat im BROWSER — fuer den Innendienst.
 *
 * Der Server-Endpunkt (api/unterweisung-zertifikat.ts) sendet das Blatt
 * nach dem Abschluss an den Teilnehmer. Scheitert das (oder ist es nie
 * gelaufen), braucht der Innendienst zwei Wege, die NICHT am Endpunkt
 * haengen: das Blatt selbst herunterladen und es erneut senden — ueber
 * denselben Versandweg wie jeder Bericht (sendReportEmail → /api/send-email
 * mit dem eigenen JWT). Das PDF entsteht dabei aus denselben eingefrorenen
 * Angaben der Teilnahme wie auf dem Server, nur hier im Browser.
 *
 * Schreibrechte: `sifa_teilnahmen` ist fuer angemeldete Nutzer bis auf
 * `mitarbeiter_id`, `zertifikat_fehler`, `zertifikat_gesendet_am`,
 * `zertifikat_pfad` und `updated_at` gesperrt (Trigger sifa_teilnahmen_schutz
 * in supabase/sifa_unterweisungen_rpc.sql). Genau diese Spalten werden hier
 * angefasst — `zertifikat_pfad` bleibt unveraendert, weil der Innendienst
 * das Blatt nicht im Bucket ablegt (dafuer fehlt ihm der Schreibpfad).
 */

/** Was vom Buero auf dem Blatt steht (Profil des Projektbesitzers). */
export interface ZertifikatBuero {
  name: string;
  adresse?: string | null;
  logoDataUrl?: string | null;
}

/** Dateiname des Downloads — dieselbe Bereinigung wie der Bucket-Pfad des Endpunkts. */
export const zertifikatDateiname = (zertifikatNr: string): string =>
  `Zertifikat_${(zertifikatNr || '').trim().replace(/[^\w.-]+/g, '_') || 'Unterweisung'}.pdf`;

/** Betreff — wortgleich mit dem Endpunkt, damit der Sendeverlauf beide gleich zeigt. */
export const zertifikatBetreff = (unterweisungTitel: string, zertifikatNr: string): string =>
  `Ihr Nachweis: ${(unterweisungTitel || 'Unterweisung').trim()} (${(zertifikatNr || '').trim()})`;

/** Anrede aus den eingefrorenen Angaben; ohne Namen neutral. */
export const zertifikatAnrede = (t: Pick<SifaTeilnahme, 'vorname' | 'nachname'>): string =>
  [t.vorname, t.nachname].map((s) => (s || '').trim()).filter(Boolean).join(' ') || 'Teilnehmer/in';

/** „2026-09-07" → „07.09.2026" per Zeichenkette — kein new Date auf ein reines Datum. */
const datumDe = (iso: string | null | undefined): string | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso || '').trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
};

/**
 * Mailtext in Sie-Form — derselbe Wortlaut wie im Endpunkt, damit der
 * Teilnehmer beim Nachsenden dieselbe Mail bekommt wie beim ersten Mal.
 */
export const zertifikatMailText = (args: {
  anrede: string;
  unterweisungTitel: string;
  zertifikatNr: string;
  gueltigBis?: string | null;
  bueroName: string;
}): { text: string; html: string } => {
  const gueltig = datumDe(args.gueltigBis);
  const zeilen = [
    `Guten Tag ${args.anrede},`,
    '',
    `Sie haben die Unterweisung „${args.unterweisungTitel}" erfolgreich abgeschlossen. Ihren Nachweis (${args.zertifikatNr}) finden Sie als PDF im Anhang dieser E-Mail.`,
    gueltig ? `Der Nachweis ist gültig bis ${gueltig}.` : 'Der Nachweis hat kein Ablaufdatum.',
    '',
    'Bitte bewahren Sie den Nachweis auf. Bei Fragen wenden Sie sich an Ihren Ansprechpartner im Betrieb.',
    '',
    'Mit freundlichen Grüßen',
    args.bueroName,
  ];
  const text = zeilen.join('\n');
  const html = `<div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #334155; line-height: 1.6; max-width: 600px;">${zeilen
    .map((z) => (z ? `<p style="margin:0 0 4px 0">${z}</p>` : '<br>'))
    .join('')}</div>`;
  return { text, html };
};

/**
 * ArrayBuffer → Base64 in Bloecken. `String.fromCharCode(...alleBytes)` auf
 * einmal sprengt bei einem PDF mit Logo und Unterschrift das Argumentlimit.
 */
export const arrayBufferZuBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const block = 0x8000;
  let binaer = '';
  for (let i = 0; i < bytes.length; i += block) {
    binaer += String.fromCharCode(...bytes.subarray(i, i + block));
  }
  return btoa(binaer);
};

/** Klartext, warum diese Teilnahme (noch) kein Zertifikat bekommt; null = alles da. */
export const zertifikatHindernis = (t: Pick<SifaTeilnahme, 'abgeschlossenAm' | 'bestanden' | 'zertifikatNr'>): string | null => {
  if (!t.abgeschlossenAm || t.bestanden !== true) return 'Die Unterweisung ist noch nicht bestanden abgeschlossen.';
  if (!(t.zertifikatNr || '').trim()) return 'Die Teilnahme trägt keine Zertifikat-Nummer.';
  return null;
};

/**
 * Erzeugt das Zertifikat im Browser. Laedt die VOLLSTAENDIGE Zeile nach
 * (die Liste traegt die Unterschrift bewusst nicht), holt den QR-Code und
 * die eingebettete Schrift (ohne sie: Standard-Helvetica, kein Abbruch).
 */
export const erzeugeZertifikatImBrowser = async (
  teilnahme: SifaTeilnahme,
  unterweisungTitel: string,
  buero: ZertifikatBuero,
  kundeName: string,
): Promise<{ bytes: ArrayBuffer; dateiname: string }> => {
  const hindernis = zertifikatHindernis(teilnahme);
  if (hindernis) throw new Error(hindernis);
  const zertifikatNr = (teilnahme.zertifikatNr as string).trim();

  const detail = (await fetchTeilnahmeDetail(teilnahme.id)) || teilnahme;
  const [qrDataUrl] = await Promise.all([
    QRCode.toDataURL(zertifikatNr, { width: 240, margin: 1 }),
    ensureEmbeddedFont(),
  ]);

  const bytes = erzeugeZertifikatPdf({
    bueroName: (buero.name || '').trim() || 'GefAS mbH',
    bueroAdresse: buero.adresse,
    bueroLogoDataUrl: buero.logoDataUrl,
    kundeName: (kundeName || '').trim() || 'Auftraggeber',
    titel: (unterweisungTitel || 'Unterweisung').trim(),
    vorname: detail.vorname || '',
    nachname: detail.nachname || '',
    geburtsdatum: detail.geburtsdatum,
    abgeschlossenAm: detail.abgeschlossenAm || teilnahme.abgeschlossenAm || '',
    gueltigBis: detail.gueltigBis ?? teilnahme.gueltigBis,
    zertifikatNr,
    punkte: detail.punkte ?? teilnahme.punkte ?? 0,
    punkteMax: detail.punkteMax ?? teilnahme.punkteMax ?? 0,
    unterschriftDataUrl: detail.unterschrift,
    qrDataUrl,
  });
  return { bytes, dateiname: zertifikatDateiname(zertifikatNr) };
};

/**
 * Sendet das Zertifikat (erneut) an die E-Mail der Teilnahme — ueber den
 * Berichtsversand des angemeldeten Nutzers. Danach wird die Teilnahme als
 * gesendet vermerkt und ein alter Fehlertext geloescht; der Sendeverlauf
 * bekommt einen Eintrag (Fehler dort blockieren nichts).
 */
export const sendeZertifikatErneut = async (
  teilnahme: SifaTeilnahme,
  unterweisungTitel: string,
  buero: ZertifikatBuero,
  kundeName: string,
): Promise<{ dateiname: string; empfaenger: string }> => {
  const empfaenger = (teilnahme.email || '').trim();
  if (!empfaenger) throw new Error('Die Teilnahme trägt keine E-Mail-Adresse.');

  const { bytes, dateiname } = await erzeugeZertifikatImBrowser(teilnahme, unterweisungTitel, buero, kundeName);
  const zertifikatNr = (teilnahme.zertifikatNr as string).trim();
  const titel = (unterweisungTitel || 'Unterweisung').trim();
  const bueroName = (buero.name || '').trim() || 'GefAS mbH';
  const subject = zertifikatBetreff(titel, zertifikatNr);
  const { text, html } = zertifikatMailText({
    anrede: zertifikatAnrede(teilnahme),
    unterweisungTitel: titel,
    zertifikatNr,
    gueltigBis: teilnahme.gueltigBis,
    bueroName,
  });

  await sendReportEmail({
    recipients: [empfaenger],
    subject,
    message: text,
    html,
    pdfBase64: arrayBufferZuBase64(bytes),
    filename: dateiname,
  });

  // Die Mail ist raus — ab hier darf nichts mehr wie ein Fehlschlag des
  // Versands aussehen, sonst wuerde jemand ein zweites Mal senden.
  const { error } = await supabase
    .from('sifa_teilnahmen')
    .update({ zertifikat_gesendet_am: new Date().toISOString(), zertifikat_fehler: null })
    .eq('id', teilnahme.id);
  if (error) {
    throw new Error(`Die E-Mail wurde gesendet, aber der Vermerk an der Teilnahme scheiterte: ${describeError(error)}`);
  }

  try {
    await logEmailAttempt({
      project_id: teilnahme.projectId,
      project_name: (kundeName || '').trim() || 'Auftraggeber',
      recipients: empfaenger,
      subject,
      status: 'success',
      body_html: html,
      pdf_url: teilnahme.zertifikatPfad ? `sifa-zertifikate/${teilnahme.zertifikatPfad}` : undefined,
      send_key: `zertifikat:${zertifikatNr}:erneut:${Date.now()}`,
    });
  } catch (e) {
    console.warn('Sendeverlauf fuer das Zertifikat konnte nicht geschrieben werden:', describeError(e));
  }

  return { dateiname, empfaenger };
};
